/**
 * MiniChart.jsx — dependency-free SVG charts for the Practice Performance dashboard (CT-025).
 *
 * No charting library (the bundle is already large, and the design system wants tight control of type
 * + color). Four primitives, all themed to the AlderCortex tokens:
 *   - Sparkline      inline trend line
 *   - MetricTrendCard a "big number + delta + area/line" card (the Bevel score card)
 *   - HBars          horizontal distribution bars (load by person, project pressure, …)
 *   - Columns        paired vertical columns over time (schedule pushed vs pulled)
 *
 * Series are arrays of { x:dateStr, y:number }. Single-point series render gracefully (a dot + a
 * "trend builds as history accrues" hint) so the dashboard is useful from day one.
 */

import { useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ScatterChart, Scatter, Cell, ComposedChart, BarChart, Bar } from 'recharts'

const INK = '#07272D'
const MUTE = '#8a9ba3'

// Shared Recharts styling.
const axisTick = { fontSize: 9, fill: MUTE, fontFamily: '"DM Mono", monospace' }
const tip = {
  box: { background: '#07272D', color: '#fff', borderRadius: 6, padding: '6px 9px', boxShadow: '0 4px 14px rgba(7,39,45,0.28)' },
  v: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600 },
  v2: { fontFamily: '"DM Mono",monospace', fontSize: 11 },
  d: { fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#aebfc4', marginTop: 2 },
}
function TrendTip({ active, payload, label, fmt, unit }) {
  if (!active || !payload || !payload.length) return null
  return <div style={tip.box}><div style={tip.v}>{fmt ? fmt(payload[0].value) : payload[0].value}{unit ? ' ' + unit : ''}</div><div style={tip.d}>{fmtDate(label)}</div></div>
}
function FeverTip({ active, payload }) {
  if (!active || !payload || !payload.length) return null
  const p = payload[0] && payload[0].payload; if (!p) return null
  return <div style={tip.box}><div style={tip.v}>{p.name}</div><div style={tip.d}>{p.x}% complete · {p.y}d behind</div></div>
}
function ColTip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null
  return <div style={tip.box}><div style={tip.d}>{fmtDate(label)}</div>{payload.map((p, i) => <div key={i} style={tip.v2}>{p.name}: {p.value}</div>)}</div>
}

// Modern, slightly-desaturated palette (Bevel-ish) — replaces the dated harsh red / brown tones.
export const PALETTE = {
  ink: '#07272D', mute: '#8a9ba3',
  green: '#1FB57A',   // good / throughput / steady
  blue: '#3D7DFF',    // neutral / hours
  purple: '#7B6CF6',  // load (the Bevel "cardio load" hue)
  red: '#F2555A',     // at-risk / overdue / over capacity (softer than #c0392b)
  amber: '#F5A623',   // tight / warning (modern, not brown)
  teal: '#16B5A6',
}

/**
 * InfoDot — the required plain-language explainer on every chart (CT-025 success criteria: a chart the
 * average user can't read without insider knowledge has failed). Click the ⓘ for a "what this shows /
 * how to read it / what to do" popover. `text` may be a string or an array of paragraphs.
 *
 * The popover is portaled to <body> with fixed positioning so it can never be clipped or painted over
 * by neighboring cards (each card is its own positioned box; an in-card popover gets covered).
 */
export function InfoDot({ text }) {
  const [pos, setPos] = useState(null)   // null = closed; {top,left} = open
  const btnRef = useRef(null)
  if (!text) return null
  const paras = Array.isArray(text) ? text : [text]
  const W = 260

  function toggle(e) {
    e.stopPropagation()
    if (pos) { setPos(null); return }
    const r = btnRef.current.getBoundingClientRect()
    const left = Math.max(8, Math.min(r.right - W, window.innerWidth - W - 8))
    const top = Math.min(r.bottom + 6, window.innerHeight - 20)
    setPos({ top, left })
  }

  return (
    <span style={dot.wrap}>
      <button ref={btnRef} style={dot.btn} onClick={toggle} title="What is this?" aria-label="What is this?">i</button>
      {pos && createPortal(
        <>
          <div style={dot.backdrop} onClick={() => setPos(null)} />
          <div style={{ ...dot.pop, top: pos.top, left: pos.left, width: W }} onClick={e => e.stopPropagation()}>
            {paras.map((p, i) => <div key={i} style={{ marginBottom: i < paras.length - 1 ? 6 : 0 }}>{p}</div>)}
          </div>
        </>,
        document.body,
      )}
    </span>
  )
}

function fmtDate(ymd) {
  const ms = Date.parse(ymd + 'T00:00:00Z')
  if (Number.isNaN(ms)) return ymd || ''
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/** Map y-values into an SVG path. Returns { line, area } path strings for a W×H box with padding. */
function paths(points, W, H, pad) {
  const ys = points.map(p => p.y)
  let min = Math.min(...ys), max = Math.max(...ys)
  if (min === max) { min -= 1; max += 1 }
  const x = i => points.length === 1 ? W / 2 : pad + (i * (W - 2 * pad)) / (points.length - 1)
  const y = v => H - pad - ((v - min) * (H - 2 * pad)) / (max - min)
  const pts = points.map((p, i) => [x(i), y(p.y)])
  const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ')
  const area = line + ` L${pts[pts.length - 1][0].toFixed(1)},${H - pad} L${pts[0][0].toFixed(1)},${H - pad} Z`
  return { line, area, pts }
}

/** Bevel-style ring gauge: a donut filled to `frac` (0–1), with a value in the middle + a label below. */
export function Ring({ frac = 0, color = INK, centerText, label, size = 78 }) {
  const sw = 7, r = (size - sw) / 2, cx = size / 2, cy = size / 2, c = 2 * Math.PI * r
  const off = c * (1 - Math.max(0, Math.min(1, frac || 0)))
  return (
    <div style={{ textAlign: 'center' }}>
      <svg width={size} height={size}>
        <circle cx={cx} cy={cy} r={r} fill="none" style={{ stroke: 'var(--aj-border-mid)' }} strokeWidth={sw} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} transform={`rotate(-90 ${cx} ${cy})`} />
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" style={{ fontFamily: '"DM Mono",monospace', fontSize: 16, fontWeight: 600, fill: 'var(--aj-text)' }}>{centerText}</text>
      </svg>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>{label}</div>
    </div>
  )
}

export function Sparkline({ points = [], color = INK, width = 90, height = 26 }) {
  const pp = points.filter(p => p && p.y != null)
  if (!pp.length) return <svg width={width} height={height} />
  const { line, pts } = paths(pp, width, height, 3)
  const last = pts[pts.length - 1]
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      {pp.length === 1
        ? <circle cx={width / 2} cy={height / 2} r="3" fill={color} />
        : <><path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" /><circle cx={last[0]} cy={last[1]} r="2.2" fill={color} /></>}
    </svg>
  )
}

function deltaChip(delta, dir, higherIsBetter, fmt) {
  if (delta == null) return null
  // higherIsBetter: true | false | null (neutral — e.g. Load, where ~1.0 is ideal either way).
  const good = (higherIsBetter == null || dir === 'flat') ? null : (dir === 'up') === higherIsBetter
  const color = good == null ? MUTE : good ? PALETTE.green : PALETTE.red
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '◆'
  const txt = (delta > 0 ? '+' : '') + (fmt ? fmt(delta) : delta)
  return <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color, marginLeft: 8 }}>{arrow} {txt}</span>
}

export function MetricTrendCard({ title, points = [], color = INK, fmt = v => v, unit = '', higherIsBetter = true, hint, info }) {
  const pp = points.filter(p => p && p.y != null)
  const value = pp.length ? pp[pp.length - 1].y : null
  const prev = pp.length > 1 ? pp[pp.length - 2].y : null
  const delta = prev == null ? null : Math.round((value - prev) * 100) / 100
  const dir = delta == null || delta === 0 ? 'flat' : delta > 0 ? 'up' : 'down'
  const gid = 'g_' + title.replace(/\W/g, '')
  const hasLine = pp.length > 1

  return (
    <div style={card.box}>
      <InfoDot text={info} />
      <div style={card.title}>{title}</div>
      <div style={card.valueRow}>
        <span style={{ ...card.value, color }}>{value == null ? '—' : fmt(value)}</span>
        {unit && <span style={card.unit}>{unit}</span>}
        {deltaChip(delta, dir, higherIsBetter, fmt)}
      </div>
      {hasLine ? (
        <div style={{ height: 150, marginTop: 8 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={pp} margin={{ top: 6, right: 10, bottom: 0, left: -10 }}>
              <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.22} /><stop offset="100%" stopColor={color} stopOpacity={0.02} /></linearGradient></defs>
              <CartesianGrid stroke="rgba(7,39,45,0.06)" vertical={false} />
              <XAxis dataKey="x" tickFormatter={fmtDate} tick={axisTick} tickLine={false} axisLine={{ stroke: 'rgba(7,39,45,0.12)' }} minTickGap={42} />
              <YAxis tickFormatter={v => fmt(v)} tick={axisTick} tickLine={false} axisLine={false} width={36} />
              <Tooltip content={<TrendTip fmt={fmt} unit={unit} />} />
              <Area type="monotone" dataKey="y" stroke={color} strokeWidth={2} fill={`url(#${gid})`} dot={false} activeDot={{ r: 4, stroke: '#fff', strokeWidth: 1.5 }} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div style={card.singleNote}>{pp.length === 1 ? `${fmt(value)}${unit ? ' ' + unit : ''} on ${fmtDate(pp[0].x)} · ${hint || 'trend builds as history accrues'}` : (hint || 'No data yet')}</div>
      )}
    </div>
  )
}

/**
 * FeverChart — CCPM-style portfolio scatter: x = % complete, y = working-days behind, on green/amber/
 * red bands. A dot drifting up-and-left (very behind, little done) is in trouble. Hover a dot for detail.
 */
export function FeverChart({ data = [], info, emptyText = 'No active projects' }) {
  // Sloping CCPM-style danger thresholds: the tolerance for being behind TIGHTENS as a project nears
  // completion (less runway left). Red boundary 12d→3d, amber 4d→1d across 0%→100% complete.
  const redAt = x => 12 - 0.09 * x
  const amberAt = x => 4 - 0.03 * x
  const pts = data.map(d => {
    const x = Math.round((d.pctComplete || 0) * 100), y = d.behind || 0
    const zone = y > redAt(x) ? 'red' : y > amberAt(x) ? 'amber' : 'green'
    return { x, y, name: d.name, zone }
  })
  // Filled zones: stacked areas whose cumulative tops follow the two sloping thresholds, so the
  // green / amber / red regions are painted (not just outlined). Top of red = the y-domain ceiling.
  const domainMax = Math.max(13, ...pts.map(p => p.y))
  const band = [0, 100].map(x => ({
    x,
    green: amberAt(x),                 // 0 → amber line
    amber: redAt(x) - amberAt(x),      // amber line → red line
    red: domainMax - redAt(x),         // red line → top
  }))
  return (
    <div style={bars.box}>
      <InfoDot text={info} />
      <div style={bars.title}>Project fever chart — behind vs complete</div>
      {pts.length === 0 ? <div style={bars.empty}>{emptyText}</div> : (
        <div style={{ height: 234, marginTop: 4 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={band} margin={{ top: 14, right: 18, bottom: 24, left: 2 }}>
              <CartesianGrid stroke="rgba(7,39,45,0.06)" />
              <XAxis type="number" dataKey="x" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={v => v + '%'} tick={axisTick} tickLine={false} axisLine={{ stroke: 'rgba(7,39,45,0.12)' }}
                label={{ value: 'project completion →', position: 'insideBottom', offset: -10, style: { fontSize: 9, fill: MUTE, fontFamily: '"DM Mono", monospace' } }} />
              <YAxis type="number" dataKey="y" scale="sqrt" domain={[0, domainMax]} allowDataOverflow tickFormatter={v => v + 'd'} tick={axisTick} tickLine={false} axisLine={false} width={42}
                label={{ value: 'working-days behind', angle: -90, position: 'insideLeft', offset: 14, style: { fontSize: 9, fill: MUTE, fontFamily: '"DM Mono", monospace', textAnchor: 'middle' } }} />
              <Area type="linear" dataKey="green" stackId="z" stroke="none" fill="#1FB57A" fillOpacity={0.10} isAnimationActive={false} activeDot={false} tooltipType="none" />
              <Area type="linear" dataKey="amber" stackId="z" stroke="none" fill="#F5A623" fillOpacity={0.13} isAnimationActive={false} activeDot={false} tooltipType="none" />
              <Area type="linear" dataKey="red" stackId="z" stroke="none" fill="#F2555A" fillOpacity={0.12} isAnimationActive={false} activeDot={false} tooltipType="none" />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<FeverTip />} />
              <Scatter data={pts} isAnimationActive={false}>
                {pts.map((p, i) => <Cell key={i} fill={p.zone === 'red' ? '#F2555A' : p.zone === 'amber' ? '#F5A623' : '#1FB57A'} fillOpacity={0.9} stroke="#fff" strokeWidth={1.5} />)}
              </Scatter>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      <div style={fever.legend}>Each dot is a project: <b>right</b> = closer to done, <b>higher</b> = further behind schedule. The colored bands are the danger zones — they narrow toward the right because a late project near completion has no runway left to recover. <b style={{ color: '#1FB57A' }}>Green</b> is healthy, <b style={{ color: '#F5A623' }}>amber</b> is slipping, <b style={{ color: '#F2555A' }}>red</b> needs attention. Hover any dot for the project.</div>
    </div>
  )
}

export function HBars({ title, data = [], fmt = v => v, emptyText = 'No data', info }) {
  const vals = data.map(d => Number(d.value) || 0)
  const max = Math.max(1, ...vals)
  return (
    <div style={bars.box}>
      <InfoDot text={info} />
      {title && <div style={bars.title}>{title}</div>}
      {data.length === 0 ? <div style={bars.empty}>{emptyText}</div> : data.map((d, i) => (
        <div key={i} style={bars.row}>
          <div style={bars.name} title={d.name}>{d.name}</div>
          <div style={bars.track}>
            <div style={{ ...bars.fill, width: Math.max(2, (Number(d.value) || 0) / max * 100) + '%', background: d.color || INK }} />
          </div>
          <div style={bars.val}>{d.value == null ? '—' : fmt(d.value)}{d.sub ? <span style={bars.sub}> {d.sub}</span> : null}</div>
        </div>
      ))}
    </div>
  )
}

export function Columns({ title, data = [], series = [], emptyText = 'No data', info }) {
  // data: [{ x, [key]:number }]; series: [{ key, label, color }]
  return (
    <div style={bars.box}>
      <InfoDot text={info} />
      {title && <div style={bars.title}>{title}</div>}
      {data.length === 0 ? <div style={bars.empty}>{emptyText}</div> : (
        <>
          <div style={{ height: 130 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid stroke="rgba(7,39,45,0.06)" vertical={false} />
                <XAxis dataKey="x" tickFormatter={fmtDate} tick={axisTick} tickLine={false} axisLine={{ stroke: 'rgba(7,39,45,0.12)' }} minTickGap={28} />
                <YAxis tick={axisTick} tickLine={false} axisLine={false} width={26} allowDecimals={false} />
                <Tooltip cursor={{ fill: 'rgba(7,39,45,0.04)' }} content={<ColTip />} />
                {series.map(s => <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[2, 2, 0, 0]} isAnimationActive={false} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
            {series.map(s => (
              <span key={s.key} style={bars.legend}><span style={{ ...bars.swatch, background: s.color }} />{s.label}</span>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

const dot = {
  wrap: { position: 'absolute', top: 8, right: 8, zIndex: 3 },
  btn: { width: 16, height: 16, borderRadius: '50%', border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)', color: 'var(--aj-text-muted)', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 10, fontWeight: 700, lineHeight: '14px', cursor: 'pointer', padding: 0, textAlign: 'center' },
  backdrop: { position: 'fixed', inset: 0, zIndex: 4000 },
  pop: { position: 'fixed', zIndex: 4001, background: '#07272D', color: '#eaf1f2', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, lineHeight: 1.5, padding: '10px 12px', borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.28)' },
}

const card = {
  box: { position: 'relative', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 12, padding: '14px 16px', minWidth: 0, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  title: { fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' },
  valueRow: { display: 'flex', alignItems: 'baseline', marginTop: 4, minHeight: 26 },
  value: { fontFamily: '"DM Mono",monospace', fontSize: 25, fontWeight: 600, lineHeight: 1 },
  unit: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)', marginLeft: 4 },
  hoverDate: { fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: 'var(--aj-text-muted)', marginLeft: 8 },
  chartRow: { display: 'flex', gap: 8, marginTop: 10 },
  yAxis: { display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '8px 0', width: 34, flexShrink: 0, textAlign: 'right' },
  yTick: { fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)', lineHeight: 1 },
  plotCol: { flex: 1, minWidth: 0 },
  chartWrap: { position: 'relative', cursor: 'crosshair' },
  xAxis: { display: 'flex', justifyContent: 'space-between', marginTop: 5 },
  xTick: { fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' },
  crosshair: { position: 'absolute', top: 0, bottom: 0, width: 1, background: 'var(--aj-border-mid)', transform: 'translateX(-0.5px)', pointerEvents: 'none' },
  dot: { position: 'absolute', width: 9, height: 9, borderRadius: '50%', background: 'var(--aj-bg-elevated)', border: '2px solid', transform: 'translate(-50%,-50%)', pointerEvents: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' },
  tip: { position: 'absolute', zIndex: 2, background: '#07272D', color: '#fff', fontFamily: '"DM Mono",monospace', fontSize: 10.5, padding: '4px 8px', borderRadius: 6, whiteSpace: 'nowrap', pointerEvents: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' },
  singleNote: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 10, lineHeight: 1.4 },
  foot: { fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: 'var(--aj-text-muted)', marginTop: 6 },
}

const fever = {
  axis: { fontFamily: '"DM Mono",monospace', fontSize: 8, fill: MUTE },
  label: { fontFamily: '"DM Sans",sans-serif', fontSize: 9, fontWeight: 600, fill: INK },
  legend: { fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', marginTop: 8, lineHeight: 1.4 },
}

const bars = {
  box: { position: 'relative', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: '12px 14px' },
  title: { fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 },
  empty: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', fontStyle: 'italic', padding: '4px 0' },
  row: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 },
  name: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text)', width: 130, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  track: { flex: 1, height: 8, borderRadius: 4, background: 'var(--aj-bg-muted)', overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 4 },
  val: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text)', width: 72, flexShrink: 0, textAlign: 'right' },
  sub: { color: 'var(--aj-text-muted)', fontSize: 9.5 },
  legend: { fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', display: 'flex', alignItems: 'center', gap: 5 },
  swatch: { width: 9, height: 9, borderRadius: 2, display: 'inline-block' },
}
